import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import TopBar from '../components/TopBar';
import NotifPanel from '../components/NotifPanel';
import { PublicidadCard, PublicidadPopup, type Publicidad } from '../components/PublicidadView';
import { api } from '../api/client';
import { useAuth } from '../stores/auth';
import { useFlags } from '../stores/flags';
import { useNotifStore } from '../stores/notif';
import { initPush } from '../utils/push';

// ── Clima ─────────────────────────────────────────────────────
const WMO: Record<number, { icon: string; desc: string }> = {
  0:  { icon: '☀️',  desc: 'Despejado' },
  1:  { icon: '🌤️', desc: 'Mayormente despejado' },
  2:  { icon: '⛅',  desc: 'Parcialmente nublado' },
  3:  { icon: '☁️',  desc: 'Nublado' },
  45: { icon: '🌫️', desc: 'Niebla' },
  48: { icon: '🌫️', desc: 'Niebla' },
  51: { icon: '🌦️', desc: 'Llovizna leve' },
  53: { icon: '🌦️', desc: 'Llovizna' },
  55: { icon: '🌧️', desc: 'Llovizna intensa' },
  61: { icon: '🌧️', desc: 'Lluvia leve' },
  63: { icon: '🌧️', desc: 'Lluvia' },
  65: { icon: '🌧️', desc: 'Lluvia intensa' },
  71: { icon: '❄️',  desc: 'Nieve leve' },
  73: { icon: '❄️',  desc: 'Nieve' },
  75: { icon: '❄️',  desc: 'Nieve intensa' },
  80: { icon: '🌦️', desc: 'Chubascos leves' },
  81: { icon: '🌧️', desc: 'Chubascos' },
  82: { icon: '⛈️',  desc: 'Chubascos fuertes' },
  95: { icon: '⛈️',  desc: 'Tormenta' },
  96: { icon: '⛈️',  desc: 'Tormenta con granizo' },
  99: { icon: '⛈️',  desc: 'Tormenta con granizo fuerte' },
};
function wmo(code: number) {
  return WMO[code] ?? { icon: '🌡️', desc: '–' };
}

type ClimaData = {
  temp: number; sensacion: number; humedad: number;
  viento: number; precipit: number; icon: string; desc: string; ciudad: string;
};

type SesionActiva = {
  id: number; zona_nombre: string; zona_codigo: string;
  iniciada_en: string; patente: string; tarifa_hora_centavos: number;
} | null;

// ── Emergencias ───────────────────────────────────────────────
const EMERGENCIAS = [
  { label: 'Emergencia General',      num: '911', icon: '🆘', color: '#dc2626' },
  { label: 'Bomberos',                num: '100', icon: '🚒', color: '#ff5533' },
  { label: 'Policía',                 num: '101', icon: '🚔', color: '#3b82f6' },
  { label: 'Ayuda Niño/Adolescente',  num: '102', icon: '👶', color: '#d97706' },
  { label: 'Defensa Civil',           num: '103', icon: '🛡️', color: '#32329f' },
  { label: 'Emergencia Ambiental',    num: '105', icon: '🌿', color: '#10b981' },
  { label: 'Emergencia Náutica',      num: '106', icon: '⚓', color: '#0ea5e9' },
  { label: 'Hospital',                num: '107', icon: '🚑', color: '#10b981' },
  { label: 'Violencia de Género',     num: '144', icon: '💜', color: '#ff2d78' },
];

// ── Accesos rápidos ───────────────────────────────────────────
type QuickItem = {
  icon: string; label: string; sub: string; color: string;
  to?: string; disabled?: boolean; requiresVerification?: boolean;
  flag?: string; // si está definido, el item solo se muestra cuando el flag está ON
};
const ACCESOS: QuickItem[] = [
  { icon: '📋', label: 'Nuevo reclamo',     sub: 'Informanos tu problema',            color: '#32329f', to: '/reclamos/nuevo' },
  { icon: '📦', label: 'Mis reclamos',      sub: 'Ver el estado de tus reclamos',     color: '#d97706', to: '/reclamos'       },
  { icon: '🏛️', label: 'Mis impuestos',      sub: 'Tasas, inmuebles y cuotas',         color: '#7c3aed', to: '/mis-impuestos',  flag: 'catastro_tasas', requiresVerification: true },
  { icon: '🅿️', label: 'Estacionamiento',  sub: 'Iniciar o ver tu sesión activa',    color: '#10b981', to: '/estacionar',     flag: 'estacionamiento_master', requiresVerification: true },
  { icon: '💰', label: 'Billetera',         sub: 'Saldo y movimientos',               color: '#32329f', to: '/billetera',      flag: 'estacionamiento_master', requiresVerification: true },
  { icon: '⚠️', label: 'Infracciones',     sub: 'Consultá y pagá tus infracciones',  color: '#d97706', to: '/infracciones',   flag: 'estacionamiento_master', requiresVerification: true },
  { icon: '📰', label: 'Avisos',            sub: 'Noticias desde el municipio',       color: '#10b981', to: '/avisos'         },
  { icon: '🏗️', label: 'Tablero de Obras', sub: 'Obras en tu municipio',             color: '#ea580c', to: '/obras'          },
  { icon: '📄', label: 'Guía de trámites', sub: 'Cómo hacer y sus requisitos',       color: '#10b981', to: '/tramites'       },
  { icon: '🗳️', label: 'Encuestas',        sub: 'Participá de las encuestas',        color: '#7c3aed', to: '/encuestas'      },
  { icon: '🏪', label: 'Comercios',        sub: 'Descubrí negocios de tu municipio', color: '#0891b2', to: '/comercios',      flag: 'mobile_comercios_visible' },
  { icon: '🗺️', label: 'Mapa de la ciudad', sub: 'Lugares de interés y servicios',   color: '#0d9488', to: '/mapa-poi',        flag: 'mobile_poi_mapa' },
];

// ── QuickBtn ─────────────────────────────────────────────────
function QuickBtn({ item, locked, onClick }: { item: QuickItem; locked: boolean; onClick: () => void }) {
  const isLocked = locked && !!item.requiresVerification;
  return (
    <button
      onClick={onClick}
      disabled={item.disabled}
      className="w-full flex items-center gap-4 rounded-2xl px-4 py-3.5 text-left disabled:opacity-50"
      style={{
        backgroundColor: isLocked ? '#f1f5f9' : '#ffffff',
        border: `1px solid ${isLocked ? '#cbd5e1' : '#6e6b6b'}`,
        opacity: item.disabled ? 0.5 : 1,
      }}
    >
      <div
        className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0"
        style={{
          backgroundColor: isLocked ? '#e2e8f055' : item.color + '22',
          border: `1.5px solid ${isLocked ? '#cbd5e1' : item.color + '55'}`,
        }}
      >
        <span className="text-2xl leading-none">{isLocked ? '🔒' : item.icon}</span>
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-bold text-sm leading-tight" style={{ color: isLocked ? '#94a3b8' : '#303030' }}>
          {item.label}
        </p>
        <p className="text-xs mt-0.5 leading-tight" style={{ color: isLocked ? '#cbd5e1' : '#64748b' }}>
          {isLocked ? 'Requiere identidad verificada' : item.sub}
        </p>
      </div>
      {item.disabled ? (
        <span className="text-xs font-semibold flex-shrink-0" style={{ color: '#c8c8c8' }}>Pronto</span>
      ) : isLocked ? (
        <span className="text-xs font-semibold flex-shrink-0" style={{ color: '#94a3b8' }}>›</span>
      ) : (
        <span className="text-xl font-bold flex-shrink-0" style={{ color: item.color }}>›</span>
      )}
    </button>
  );
}

// ── SosBtn (hold 2 seg) ───────────────────────────────────────
const HOLD_MS = 2000;
const TICK_MS = 40;

function SosBtn({ label, hint, bg, textColor, onActivate }: {
  label: string; hint: string; bg: string; textColor: string; onActivate: () => void;
}) {
  const [progress, setProgress] = useState(0);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const start = useCallback(() => {
    let elapsed = 0;
    setProgress(0);
    intervalRef.current = setInterval(() => {
      elapsed += TICK_MS;
      const pct = Math.min((elapsed / HOLD_MS) * 100, 100);
      setProgress(pct);
      if (pct >= 100) {
        clearInterval(intervalRef.current!);
        intervalRef.current = null;
        onActivate();
        setProgress(0);
      }
    }, TICK_MS);
  }, [onActivate]);

  const cancel = useCallback(() => {
    if (intervalRef.current) { clearInterval(intervalRef.current); intervalRef.current = null; }
    setProgress(0);
  }, []);

  useEffect(() => () => { if (intervalRef.current) clearInterval(intervalRef.current); }, []);

  return (
    <button
      onPointerDown={start}
      onPointerUp={cancel}
      onPointerLeave={cancel}
      onContextMenu={(e) => e.preventDefault()}
      className="relative flex-1 rounded-xl border-2 border-black flex flex-col items-center justify-center py-5 px-3 gap-1 overflow-hidden select-none"
      style={{ backgroundColor: bg, touchAction: 'none' }}
    >
      <div className="absolute top-0 left-0 right-0 h-1.5 bg-black/15">
        <div className="h-full bg-white/70 transition-none" style={{ width: `${progress}%` }} />
      </div>
      <span
        className="font-extrabold text-sm text-center leading-tight uppercase tracking-wide whitespace-pre-line"
        style={{ color: textColor }}
      >
        {label}
      </span>
      <span className="text-xs font-semibold mt-0.5 opacity-80" style={{ color: textColor }}>
        {hint}
      </span>
    </button>
  );
}

// ── Inicio ────────────────────────────────────────────────────
export default function Inicio() {
  const nav       = useNavigate();
  const ciudadano = useAuth((s) => s.ciudadano);
  const setCiudadano = useAuth((s) => s.setCiudadano);
  const logout    = useAuth((s) => s.logout);
  const loadFlags    = useFlags((s) => s.load);
  const clearFlags   = useFlags((s) => s.clear);
  const flagsLoaded  = useFlags((s) => s.fetchedAt);
  const flagsData    = useFlags((s) => s.flags);
  // fetchedAt === null → aún no se cargaron: mostrar todo para evitar parpadeo
  const hasFlag = (code: string) => !flagsLoaded || flagsData[code] === true;

  const [clima,        setClima]        = useState<ClimaData | null>(null);
  const [climaCarg,    setClimaCarg]    = useState(true);
  const [climaErr,     setClimaErr]     = useState<string | null>(null);
  const [sesion,        setSesion]       = useState<SesionActiva | undefined>(undefined);
  const [infrPend,      setInfrPend]     = useState(0);
  const [modalContacts, setModalContacts] = useState(false);
  const [notifOpen,     setNotifOpen]    = useState(false);
  const [pubs,          setPubs]         = useState<Publicidad[]>([]);
  const [popupIdx,      setPopupIdx]     = useState(0);
  const [refreshing,    setRefreshing]   = useState(false);

  const notifCount = useNotifStore((s) => s.count);
  const setNotifCount = useNotifStore((s) => s.setCount);

  // Clima vía proxy PHP → Open-Meteo (evita CORS/SW issues en browser)
  useEffect(() => {
    if (!ciudadano?.municipio_id) return;
    let activo = true;
    (async () => {
      try {
        const d = await api<{ current: any; ciudad: string }>(
          `/municipios?action=clima&municipio_id=${ciudadano.municipio_id}`,
          { auth: false }
        );
        const c = d.current;
        if (activo) {
          setClima({
            temp:      Math.round(c.temperature_2m),
            sensacion: Math.round(c.apparent_temperature),
            humedad:   c.relative_humidity_2m,
            viento:    Math.round(c.wind_speed_10m),
            precipit:  c.precipitation ?? 0,
            ciudad:    d.ciudad,
            ...wmo(c.weather_code),
          });
        }
      } catch (e: any) {
        if (activo) setClimaErr(e?.message ?? 'sin conexión');
      } finally {
        if (activo) setClimaCarg(false);
      }
    })();
    return () => { activo = false; };
  }, [ciudadano?.municipio_id]);

  // Feature flags del municipio
  useEffect(() => {
    if (ciudadano?.municipio_id) loadFlags(ciudadano.municipio_id);
  }, [ciudadano?.municipio_id, loadFlags]);

  // Estado: sesión activa + infracciones + notificaciones + publicidades
  const cargar = useCallback(async () => {
    await Promise.allSettled([
      api<SesionActiva>('/estacionamiento?action=sesion_activa')
        .then((d) => setSesion(d)).catch(() => setSesion(null)),
      api<any[]>('/infracciones?action=mis_infracciones')
        .then((l) => setInfrPend(l.filter((i: any) => i.estado === 'pendiente').length)).catch(() => {}),
      api<{ items: any[]; no_leidas: number }>('/notificaciones')
        .then((d) => setNotifCount(d.no_leidas)).catch(() => {}),
      api<{ items: Publicidad[] }>('/publicidad?action=activos')
        .then((d) => setPubs(d.items)).catch(() => {}),
    ]);
  }, [setNotifCount]);

  useEffect(() => {
    cargar();
    initPush().catch(() => {});
  }, [cargar]);

  // Pull-to-refresh
  const touchStartY = useRef(0);
  const onTouchStart = useCallback((e: React.TouchEvent) => {
    touchStartY.current = e.touches[0].clientY;
  }, []);
  const onTouchEnd = useCallback(async (e: React.TouchEvent) => {
    const delta = e.changedTouches[0].clientY - touchStartY.current;
    if (delta < 80 || window.scrollY > 0 || refreshing) return;
    setRefreshing(true);
    try {
      const me = await api<typeof ciudadano>('/auth?action=me').catch(() => null);
      if (me) setCiudadano(me as any);
      await cargar();
    } finally {
      setRefreshing(false);
    }
  }, [cargar, ciudadano, setCiudadano, refreshing]);

  const minutos = sesion
    ? Math.floor((Date.now() - new Date(sesion.iniciada_en.replace(' ', 'T')).getTime()) / 60000)
    : 0;

  return (
    <>
      <TopBar
        title="CiudadApp"
        right={
          <div className="flex items-center gap-2">
            {/* Campanita */}
            <button
              onClick={() => setNotifOpen(true)}
              className="relative w-8 h-8 flex items-center justify-center rounded-full"
              style={{ backgroundColor: '#e8e8e8' }}
              aria-label="Notificaciones"
            >
              <span className="text-base leading-none">🔔</span>
              {notifCount > 0 && (
                <span
                  className="absolute -top-0.5 -right-0.5 min-w-4 h-4 rounded-full flex items-center justify-center text-white font-bold"
                  style={{ backgroundColor: '#dc2626', fontSize: 9, paddingInline: 3 }}
                >
                  {notifCount > 9 ? '9+' : notifCount}
                </span>
              )}
            </button>

            {/* Perfil */}
            <button
              onClick={() => nav('/perfil')}
              className="w-8 h-8 rounded-full flex items-center justify-center text-base leading-none"
              style={{ backgroundColor: '#e8e8e8' }}
              aria-label="Mi perfil"
            >
              😊
            </button>
          </div>
        }
      />

      <main
        className="max-w-md mx-auto pb-10 overflow-x-hidden"
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
      >
        {/* Indicador pull-to-refresh */}
        {refreshing && (
          <div className="flex justify-center py-3">
            <svg className="animate-spin w-5 h-5 text-gray-400" viewBox="0 0 24 24" fill="none">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/>
            </svg>
          </div>
        )}

        {/* Banner: cuenta sin verificar — solo cuando sabemos con certeza que es 0 */}
        {ciudadano && (ciudadano.identidad_verificada === 0 || ciudadano.identidad_verificada === false) && (
          <button
            onClick={() => nav('/perfil')}
            className="w-full flex items-center gap-3 px-4 py-3 text-left"
            style={{ backgroundColor: '#fef9c3', borderBottom: '1px solid #fde68a' }}
          >
            <span className="text-xl leading-none flex-shrink-0">⚠️</span>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-bold text-amber-800 leading-tight">Cuenta sin verificar</p>
              <p className="text-xs text-amber-700 leading-tight mt-0.5">
                Algunas funciones estarán bloqueadas hasta que un agente municipal verifique tu identidad.
              </p>
            </div>
            <span className="text-amber-600 font-bold flex-shrink-0">›</span>
          </button>
        )}

        {/* Clima */}
        <div className="px-5 pt-3 pb-1">
          {climaCarg ? (
            <p className="text-gray-500 text-sm h-12 flex items-center">Cargando clima…</p>
          ) : clima ? (
            <>
              <div className="flex items-center justify-between">
                <div>
                  <span className="text-2xl font-extrabold text-gray-900 tracking-tight">
                    {clima.icon} {clima.temp}°C
                  </span>
                  <p className="text-sm text-gray-600 mt-0.5">{clima.desc} · {clima.ciudad}</p>
                </div>
                <button
                  onClick={() => nav('/clima')}
                  className="flex-shrink-0 rounded-xl px-3 py-1.5 text-sm font-semibold"
                  style={{ backgroundColor: '#e0e0e0', color: '#505050', border: '1px solid #c8c8c8' }}
                >
                  7 días ›
                </button>
              </div>
              <div className="grid grid-cols-4 gap-1.5 mt-2.5">
                {[
                  { e: '🌡️', v: `${clima.sensacion}°`, l: 'Sens.' },
                  { e: '💧', v: `${clima.humedad}%`,   l: 'Hum.' },
                  { e: '💨', v: `${clima.viento}km`,   l: 'Viento' },
                  { e: '🌂', v: `${clima.precipit}mm`, l: 'Lluvia' },
                ].map(({ e, v, l }) => (
                  <div key={l} className="bg-white rounded-xl py-2 px-1 flex flex-col items-center gap-0.5 shadow-sm border border-gray-100">
                    <span className="text-base leading-none">{e}</span>
                    <span className="font-bold text-sm text-gray-900">{v}</span>
                    <span className="text-xs text-gray-400">{l}</span>
                  </div>
                ))}
              </div>
            </>
          ) : climaErr ? (
            <p className="text-xs text-gray-400 h-8 flex items-center">
              🌤️ Clima no disponible — {climaErr}
            </p>
          ) : null}
        </div>

        {/* Publicidades — carrusel entre clima y emergencias */}
        {(() => {
          const cards = pubs.filter((p) => p.tipo !== 'popup');
          if (cards.length === 0) return null;
          const multi = cards.length > 1;
          return (
            <div className="pt-3 overflow-hidden">
              <div
                className="flex gap-3 overflow-x-auto"
                style={{
                  scrollSnapType: 'x mandatory',
                  WebkitOverflowScrolling: 'touch',
                  scrollbarWidth: 'none',
                  msOverflowStyle: 'none',
                  paddingLeft: 16,
                  paddingRight: multi ? 80 : 16,
                } as React.CSSProperties}
              >
                {cards.map((p) => (
                  <div
                    key={p.id}
                    style={{
                      scrollSnapAlign: 'start',
                      flexShrink: 0,
                      // multi: deja ~44px de peek del siguiente; single: mismo ancho que la grilla 3x3
                      width: multi ? 'calc(100% - 44px)' : '100%',
                    }}
                  >
                    <PublicidadCard pub={p} />
                  </div>
                ))}
              </div>
              {multi && (
                <div className="flex justify-center gap-1.5 mt-2">
                  {cards.map((_, i) => (
                    <div key={i} className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: '#d1d5db' }} />
                  ))}
                </div>
              )}
            </div>
          );
        })()}

        {/* Alerts de estado (solo si hay algo que mostrar) */}
        {(sesion || infrPend > 0) && (
          <div className="px-4 space-y-2 pt-1">
            {sesion && (
              <button
                onClick={() => nav('/estacionar')}
                className="w-full bg-green-100 border-2 border-green-400 rounded-xl p-3 text-left"
              >
                <p className="text-xs text-green-700 font-bold uppercase tracking-wide">🅿️ Estacionamiento activo</p>
                <div className="flex items-center justify-between mt-0.5">
                  <span className="font-bold text-base text-gray-900">{sesion.patente}</span>
                  <span className="text-xs text-green-700">Zona {sesion.zona_codigo} · {minutos} min</span>
                </div>
              </button>
            )}

            {infrPend > 0 && (
              <button
                onClick={() => nav('/infracciones')}
                className="w-full bg-red-100 border border-red-400 rounded-xl p-3 text-left"
              >
                <p className="font-semibold text-red-700 text-sm">
                  ⚠️ {infrPend} infracción{infrPend !== 1 ? 'es' : ''} pendiente{infrPend !== 1 ? 's' : ''} — Tocá para pagar
                </p>
              </button>
            )}
          </div>
        )}

        {/* Emergencias */}
        <div className="px-4 pt-4">
          <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2 text-center">
            Emergencias — tocá para llamar
          </p>
          <div className="grid grid-cols-3 gap-1.5">
            {EMERGENCIAS.map((e) => (
              <a
                key={e.num}
                href={`tel:${e.num}`}
                className="relative flex flex-col items-center justify-center rounded-xl p-2 border-2 no-underline"
                style={{ backgroundColor: '#ffffff', borderColor: e.color, aspectRatio: '1 / 1' }}
              >
                <span className="absolute top-1.5 right-1.5 w-1.5 h-1.5 rounded-full" style={{ backgroundColor: e.color }} />
                <span className="text-4xl leading-none mb-0.5">{e.icon}</span>
                <span className="font-bold text-center leading-tight" style={{ color: e.color, fontSize: '9px', lineHeight: '1.2' }}>
                  {e.label}
                </span>
                <span className="font-extrabold text-xs mt-0.5" style={{ color: e.color }}>{e.num}</span>
              </a>
            ))}
          </div>
        </div>

        {/* SOS — solo si seguridad_master está habilitado */}
        {hasFlag('seguridad_master') && (
          <div className="px-4 pt-3">
            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2 text-center">
              🆘 Asistencia inmediata
            </p>
            <div className="flex gap-3">
              {hasFlag('mobile_seguridad_contactos') && (
                <SosBtn
                  label={'AVISO A\nCONTACTOS'}
                  hint="Mantener 2 seg"
                  bg="#fb923c"
                  textColor="#1c1917"
                  onActivate={() => setModalContacts(true)}
                />
              )}
              {hasFlag('mobile_seguridad_panico') && (
                <SosBtn
                  label={'EMERGENCIA\nPOLICIAL'}
                  hint="Mantener 2 seg"
                  bg="#dc2626"
                  textColor="#ffffff"
                  onActivate={() => nav('/emergencia')}
                />
              )}
            </div>
          </div>
        )}

        {/* Accesos rápidos */}
        <div className="px-4 pt-6 space-y-2.5">
          <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-1">Servicios municipales</p>
          {ACCESOS.filter((item) => !item.flag || hasFlag(item.flag)).map((item) => (
            <QuickBtn
              key={item.label}
              item={item}
              locked={!ciudadano?.identidad_verificada}
              onClick={() => { if (item.to) nav(item.to); }}
            />
          ))}
        </div>

        {/* Cerrar sesión */}
        <div className="px-4 pt-4 pb-2">
          <button
            onClick={() => { clearFlags(); logout(); nav('/login', { replace: true }); }}
            className="w-full flex items-center justify-center gap-2 rounded-2xl px-4 py-3.5 text-sm font-semibold"
            style={{ color: '#dc2626', backgroundColor: '#fff5f5', border: '1px solid #fecaca' }}
          >
            🚪 Cerrar sesión
          </button>
        </div>

      </main>

      {/* Publicidad popup — muestra el primero no visto en esta sesión */}
      {(() => {
        const popups = pubs.filter((p) => p.tipo === 'popup');
        const visible = popups[popupIdx];
        if (!visible || sessionStorage.getItem(`pub_seen_${visible.id}`)) return null;
        return (
          <PublicidadPopup
            pub={visible}
            onClose={() => setPopupIdx((i) => i + 1)}
          />
        );
      })()}

      <NotifPanel open={notifOpen} onClose={() => setNotifOpen(false)} />

      {/* Modal: sin contactos de emergencia */}
      {modalContacts && (
        <>
          <div
            className="fixed inset-0 z-40"
            style={{ backgroundColor: 'rgba(0,0,0,0.55)' }}
            onClick={() => setModalContacts(false)}
          />
          <div
            className="fixed inset-x-4 z-50 bg-white rounded-2xl shadow-2xl overflow-hidden"
            style={{ top: '50%', transform: 'translateY(-50%)', maxWidth: 360, margin: '0 auto' }}
          >
            <div className="px-5 pt-6 pb-5 space-y-4">
              <div className="text-center">
                <span className="text-5xl leading-none">🆘</span>
                <p className="text-lg font-extrabold text-gray-900 mt-3">Contactos de emergencia</p>
                <p className="text-sm text-gray-500 mt-2 leading-relaxed">
                  No tenés contactos configurados.<br />
                  Agregá hasta 3 personas desde tu perfil para que reciban una alerta cuando lo necesites.
                </p>
              </div>
              <button
                onClick={() => { setModalContacts(false); nav('/perfil'); }}
                className="w-full text-white rounded-xl py-3.5 font-bold text-base"
                style={{ backgroundColor: '#32329f' }}
              >
                Ir a configurar contactos
              </button>
              <button
                onClick={() => setModalContacts(false)}
                className="w-full border border-app rounded-xl py-3 text-sm text-gray-500 font-medium"
              >
                Cancelar
              </button>
            </div>
          </div>
        </>
      )}
    </>
  );
}
